import React, { useMemo, useState } from 'react';
import { DashboardPortfolio } from '../../shared/ui-models/occupancy-dashboard.ui-model';
import {
  ConversionMode,
  DashboardFilters,
  EMPTY_FILTERS,
  GroupKey,
  RateLadder,
  UnitRow,
  computeModel,
  computePace
} from './engine';
import { CostDim, MultiSelect, Option, SortState, ValueBasis } from './components';
import { C, dateToIdx, fmtDate, fmtDay, fmtNum, idxToInput, uniq } from './theme';
import { OccupancyTab } from './OccupancyTab';
import { BlockedTab } from './BlockedTab';
import { CostTab } from './CostTab';
import { PaceTab } from './PaceTab';

type Tab = 'occupancy' | 'blocked' | 'cost' | 'pace';

const TABS: { k: Tab; label: string }[] = [
  { k: 'occupancy', label: 'Occupancy' },
  { k: 'blocked', label: 'Blocked dates' },
  { k: 'cost', label: 'Block cost' },
  { k: 'pace', label: 'Booking pace' }
];

const FILTER_FIELDS: { key: keyof DashboardFilters; label: string }[] = [
  { key: 'unitTypeGroup', label: 'Unit type group' },
  { key: 'unitType', label: 'Unit type' },
  { key: 'area', label: 'Area' },
  { key: 'view', label: 'View' },
  { key: 'months', label: 'Months' },
  { key: 'project', label: 'Project' },
  { key: 'building', label: 'Building name' },
  { key: 'landlord', label: 'Landlord' },
  { key: 'currency', label: 'Currency' },
  { key: 'nationality', label: 'Nationality' },
  { key: 'source', label: 'Source of booking' },
  { key: 'reason', label: 'Block type' },
  { key: 'owner', label: 'Block raised by' }
];

interface Chip {
  k: string;
  label: string;
  clear?: () => void;
}

const csvCell = (c: unknown) =>
  `"${String(c !== null && c !== undefined ? c : '').replace(/"/g, '""')}"`;

export function OccupancyDashboard({
  portfolio,
  rates
}: {
  portfolio: DashboardPortfolio;
  rates: RateLadder;
}) {
  const { calendar: cal, units } = portfolio;
  const clampIdx = (i: number) => Math.max(0, Math.min(cal.totalDays - 1, i));
  const currentMonth = cal.monthOfDay[clampIdx(cal.today)];
  const monthStart = cal.monthFirstDay[currentMonth];
  const monthEnd = monthStart + cal.monthLength[currentMonth] - 1;

  const [tab, setTab] = useState<Tab>('occupancy');
  const [showFilters, setShowFilters] = useState(true);
  const [from, setFrom] = useState(idxToInput(cal, monthStart));
  const [to, setTo] = useState(idxToInput(cal, monthEnd));
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState<GroupKey>('project');
  const [valueBasis, setValueBasis] = useState<ValueBasis>('expected');
  const [conversionMode, setConversionMode] = useState<ConversionMode>('fixed');
  const [fixedRate, setFixedRate] = useState(60);
  const [costDim, setCostDim] = useState<CostDim>('reason');
  const [paceMonth, setPaceMonth] = useState(currentMonth);
  const [sortOcc, setSortOcc] = useState<SortState>({ key: 'net', dir: 'desc' });
  const [sortBlk, setSortBlk] = useState<SortState>({ key: 'expectedLoss', dir: 'desc' });
  const [draft, setDraft] = useState<DashboardFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<DashboardFilters>(EMPTY_FILTERS);

  const options = useMemo(() => {
    const of = (values: string[]): Option[] => uniq(values).map((v) => ({ value: v, label: v }));
    const blocks = units.flatMap((u) => u.blocks);
    const byField: Record<keyof DashboardFilters, Option[]> = {
      unitTypeGroup: of(units.map((u) => u.unitTypeGroup)),
      unitType: of(units.map((u) => u.unitType)),
      area: of(units.map((u) => u.area)),
      view: of(units.map((u) => u.view)),
      months: cal.months.map((m, i) => ({ value: String(i), label: m.long })),
      project: of(units.map((u) => u.project)),
      building: of(units.map((u) => u.building)),
      landlord: of(units.map((u) => u.landlord)),
      currency: of(units.map((u) => u.currency)),
      nationality: of(portfolio.nationalities),
      source: of(portfolio.sources),
      reason: portfolio.reasons.map((v) => ({ value: v, label: v })),
      owner: of(blocks.map((b) => b.owner))
    };
    return byField;
  }, [portfolio, units, cal]);

  const fromIdx = clampIdx(dateToIdx(cal, from));
  const toIdx = Math.max(fromIdx, clampIdx(dateToIdx(cal, to)));

  const model = useMemo(
    () =>
      computeModel(portfolio, rates, applied, {
        fromIdx,
        toIdx,
        groupBy,
        conversionMode,
        fixedRate
      }),
    [portfolio, rates, applied, fromIdx, toIdx, groupBy, conversionMode, fixedRate]
  );

  const pace = useMemo(
    () => computePace(portfolio, applied, paceMonth),
    [portfolio, applied, paceMonth]
  );

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sort = tab === 'occupancy' ? sortOcc : sortBlk;
    const base = q
      ? model.rows.filter((r) => r.name.toLowerCase().includes(q) || r.id.toLowerCase().includes(q))
      : model.rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...base].sort((a: UnitRow, b: UnitRow) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === 'string') return av.localeCompare(String(bv)) * dir;
      return (
        ((av === null || av === undefined ? -1 : av) -
          (bv === null || bv === undefined ? -1 : bv)) *
        dir
      );
    });
  }, [model.rows, search, sortOcc, sortBlk, tab]);

  const chips = useMemo(() => {
    const out: Chip[] = [
      { k: 'from', label: `From: ${fmtDate(from)}` },
      { k: 'to', label: `To: ${fmtDate(to)}` }
    ];
    FILTER_FIELDS.forEach(({ key }) =>
      applied[key].forEach((v) =>
        out.push({
          k: `${key}-${v}`,
          label: key === 'months' ? cal.months[Number(v)].long : v,
          clear: () => {
            const next = { ...applied, [key]: applied[key].filter((x) => x !== v) };
            setApplied(next);
            setDraft(next);
          }
        })
      )
    );
    return out;
  }, [applied, from, to, cal]);

  // Keyed by the long month name: short labels repeat once the window spans more than a year.
  const band = {
    x1: cal.months[cal.monthOfDay[fromIdx]].long,
    x2: cal.months[cal.monthOfDay[toIdx]].long
  };
  const todayLabel = cal.months[currentMonth].long;

  const exportCsv = () => {
    let head: string[];
    let body: (string | number)[][];
    if (tab === 'cost') {
      head = [
        'Block ID',
        'Unit ID',
        'Unit name',
        'Project',
        'Area',
        'Unit type',
        'Block type',
        'Description',
        'Raised by',
        'Cost class',
        'Start',
        'End',
        'Nights in range',
        'Exposure EGP',
        'Expected loss EGP'
      ];
      body = model.costRecords.map((r) => [
        r.id,
        r.unitId,
        r.unitName,
        r.project,
        r.area,
        r.unitType,
        r.reason,
        r.description,
        r.owner,
        r.costClass,
        idxToInput(cal, r.start),
        idxToInput(cal, r.start + r.nights - 1),
        r.nightsInRange,
        Math.round(r.exposure),
        Math.round(r.expectedLoss)
      ]);
    } else if (tab === 'occupancy') {
      head = [
        'Name',
        'Unit ID',
        'Project',
        'Area',
        'View',
        'Unit type',
        'Gross occupancy %',
        'Net occupancy %',
        'Blocked',
        'Sold',
        'Available',
        'Total',
        'ADR',
        'RevPAR'
      ];
      body = visibleRows.map((r) => [
        r.name,
        r.id,
        r.project,
        r.area,
        r.view,
        r.unitType,
        r.gross?.toFixed(2) ?? '',
        r.net?.toFixed(2) ?? '',
        r.blocked,
        r.sold,
        r.available,
        r.total,
        r.adr?.toFixed(0) ?? '',
        r.revpar?.toFixed(0) ?? ''
      ]);
    } else {
      head = [
        'Name',
        'Unit ID',
        'Project',
        'Area',
        'Unit type',
        'Block share %',
        'Blocked nights',
        'Blocks',
        'Avg length',
        'Longest',
        'Blocked now',
        'Ends on',
        'Exposure EGP',
        'Expected loss EGP',
        'Total nights'
      ];
      body = visibleRows.map((r) => [
        r.name,
        r.id,
        r.project,
        r.area,
        r.unitType,
        r.blockPct?.toFixed(2) ?? '',
        r.blocked,
        r.blockCount,
        r.avgBlock.toFixed(1),
        r.longestBlock,
        r.currentBlock ? 'Yes' : 'No',
        r.currentBlock ? idxToInput(cal, r.currentBlock.endsOn) : '',
        Math.round(r.exposure),
        Math.round(r.expectedLoss),
        r.total
      ]);
    }
    const csv = [head, ...body].map((r) => r.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tab}-${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const t = model.totals;
  const drag = t.net !== null && t.gross !== null ? t.net - t.gross : null;
  const reportYear = cal.months[currentMonth].y;

  return (
    <div className="min-h-screen w-full" style={{ background: C.page, color: C.ink }}>
      <div className="mx-auto max-w-[1600px] px-6 py-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-recoleta font-semibold text-neutral-500">
              Occupancy Rate
            </h1>
            <p className="mt-1 text-sm" style={{ color: C.muted }}>
              {fmtDate(from)}
              {' – '}
              {fmtDate(to)}
              {' \xB7 '}
              {t.units}
              {' units \xB7 '}
              {fmtNum(t.total)}
              {' unit-nights \xB7 report date '}
              {fmtDay(cal, cal.today)} {reportYear}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowFilters((s) => !s)}
              className="rounded-md border bg-white px-4 py-2 text-sm font-medium"
              style={{ borderColor: C.line }}
            >
              {showFilters ? 'Hide filters' : 'Show filters'}
            </button>
            <button
              type="button"
              onClick={exportCsv}
              className="rounded-md px-4 py-2 text-sm font-medium text-white"
              style={{ background: C.green }}
            >
              Export
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 rounded-lg border bg-white p-4" style={{ borderColor: C.line }}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {FILTER_FIELDS.map(({ key, label }) => (
                <MultiSelect
                  key={key}
                  label={label}
                  options={options[key]}
                  value={draft[key]}
                  onChange={(v) => setDraft({ ...draft, [key]: v })}
                />
              ))}
              <label
                className="flex items-center gap-2 rounded-md border bg-white px-3 py-1.5 text-sm"
                style={{ borderColor: C.line }}
              >
                <span className="text-xs" style={{ color: C.muted }}>
                  From
                </span>
                <input
                  type="date"
                  value={from}
                  min={idxToInput(cal, 0)}
                  max={to}
                  onChange={(e) => e.target.value && setFrom(e.target.value)}
                  className="w-full bg-transparent text-sm outline-none"
                />
              </label>
              <label
                className="flex items-center gap-2 rounded-md border bg-white px-3 py-1.5 text-sm"
                style={{ borderColor: C.line }}
              >
                <span className="text-xs" style={{ color: C.muted }}>
                  To
                </span>
                <input
                  type="date"
                  value={to}
                  min={from}
                  max={idxToInput(cal, cal.totalDays - 1)}
                  onChange={(e) => e.target.value && setTo(e.target.value)}
                  className="w-full bg-transparent text-sm outline-none"
                />
              </label>
              <div className="flex items-center gap-2 lg:col-span-3">
                <button
                  type="button"
                  onClick={() => setApplied(draft)}
                  className="rounded-md px-6 py-2 text-sm font-medium text-white"
                  style={{ background: C.green }}
                >
                  Apply
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDraft(EMPTY_FILTERS);
                    setApplied(EMPTY_FILTERS);
                  }}
                  className="rounded-md border px-6 py-2 text-sm font-medium"
                  style={{ borderColor: C.line, color: C.muted }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {chips.map((c) => (
            <span
              key={c.k}
              className="inline-flex items-center gap-1.5 rounded-full border bg-white px-2.5 py-1 text-xs"
              style={{ borderColor: C.line }}
            >
              {c.label}
              {c.clear && (
                <button
                  type="button"
                  onClick={c.clear}
                  className="font-semibold"
                  style={{ color: C.muted }}
                  aria-label={`Remove ${c.label}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-6 border-b" style={{ borderColor: C.line }}>
          {TABS.map((x) => (
            <button
              type="button"
              key={x.k}
              onClick={() => setTab(x.k)}
              className="relative pb-3 text-sm font-medium"
              style={{ color: tab === x.k ? C.green : C.muted }}
            >
              {x.label}
              {tab === x.k && (
                <span
                  className="absolute inset-x-0 -bottom-px h-0.5"
                  style={{ background: C.green }}
                />
              )}
            </button>
          ))}
        </div>

        {model.stayLevelFilter && (
          <p
            className="mt-4 rounded-md border bg-white px-3 py-2 text-xs"
            style={{ borderColor: C.line, color: C.muted }}
          >
            {'Nationality and source filter stay nights only. '}
            {fmtNum(model.stayNightsExcluded)}
            {
              ' sold nights fall outside the selection and are counted as available in the denominator, so occupancy reads as share of inventory won by that segment.'
            }
          </p>
        )}

        {tab === 'occupancy' && (
          <OccupancyTab
            t={t}
            drag={drag}
            model={model}
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            band={band}
            todayLabel={todayLabel}
            rows={visibleRows}
            sort={sortOcc}
            setSort={setSortOcc}
            search={search}
            setSearch={setSearch}
            cal={cal}
          />
        )}
        {tab === 'blocked' && (
          <BlockedTab
            t={t}
            model={model}
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            band={band}
            todayLabel={todayLabel}
            rows={visibleRows}
            sort={sortBlk}
            setSort={setSortBlk}
            search={search}
            setSearch={setSearch}
            valueBasis={valueBasis}
            setValueBasis={setValueBasis}
            conversionMode={conversionMode}
            setConversionMode={setConversionMode}
            fixedRate={fixedRate}
            setFixedRate={setFixedRate}
            cal={cal}
          />
        )}
        {tab === 'cost' && (
          <CostTab
            t={t}
            model={model}
            valueBasis={valueBasis}
            setValueBasis={setValueBasis}
            costDim={costDim}
            setCostDim={setCostDim}
            conversionMode={conversionMode}
            setConversionMode={setConversionMode}
            fixedRate={fixedRate}
            setFixedRate={setFixedRate}
            cal={cal}
          />
        )}
        {tab === 'pace' && (
          <PaceTab pace={pace} paceMonth={paceMonth} setPaceMonth={setPaceMonth} cal={cal} />
        )}
      </div>
    </div>
  );
}
